import Cookies from 'js-cookie';
import { lazy, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import SuspenseWithLoader from './components/common/SuspenseWithLoader';
import MainLayout from './components/layouts/MainLayout';
import useAuth from './hooks/useAuth';
import IndexPage from './pages';
import { SideBarRouteOption } from './types';
import { ROLE_TO_ROUTES } from './utils/constants';
import insuranceRoutes from './utils/routes/insuranceRoutes';
import mohAdminRoutes from './utils/routes/mohAdminRoutes';

const AcceptInvitation = lazy(() => import('@/pages/auth/AcceptInvitation.tsx'));
const VerifyEmail = lazy(() => import('@/pages/auth/VerifyEmail.tsx'));
const LoginPage = lazy(() => import('@/pages/auth/login'));
const ResetPassword = lazy(() => import('@/pages/auth/reset-password/ResetPassword.tsx'));
const VerifyResetCode = lazy(() => import('@/pages/auth/reset-password/VerifyResetCode.tsx'));
const NotFound = lazy(() => import('./pages/NotFound'));
const InsuranceAnalyticsPage = lazy(() => import('./pages/insurance/analytics'));
const InsuranceServiceRequests = lazy(() => import('./pages/insurance/approval-requests'));
const MOHAdminAnalytics = lazy(() => import('./pages/moh-admin/analytics'));
const InsurancesPage = lazy(() => import('./pages/moh-admin/insurances'));
const CreateInsuranceInstitution = lazy(() => import('./pages/moh-admin/insurances/new'));
const MOHAdminSystemUsers = lazy(() => import('./pages/moh-admin/system-settings/MOHAdminSystemUsers'));
const ProfileView = lazy(() => import('./pages/profile/Index'));
const ViewBillPage = lazy(() => import('./pages/shared/bills/view-bill'));
const InsuranceSettings = lazy(() => import('./pages/insurance/settings'));
const MOHAdminRoles = lazy(() => import('./pages/moh-admin/system-settings/roles/index'));
const ReportsPage = lazy(() => import('./pages/shared/reports/index'));
const RMPractitionerPage = lazy(() => import('./pages/moh-admin/(rssb-mocks)/practitioner'));
const RMFacilityPage = lazy(() => import('./pages/moh-admin/(rssb-mocks)/facility/index'));
const RMVouchersPage = lazy(() => import('./pages/moh-admin/(rssb-mocks)/vouchers/index'));
const RMReportsPage = lazy(() => import('./pages/moh-admin/(rssb-mocks)/reports/index'));
const MOHAdminTokensPage = lazy(() => import('./pages/moh-admin/system-settings/tokens'));
const EligibilityCheckHistoryPage = lazy(() => import('./pages/shared/eligibiity-check-history/index'));
const EligibilityTestPage = lazy(() => import('./pages/moh-admin/eligibility-test/index'));
const ApprovalHistoryPage = lazy(() => import('./pages/shared/approval-history/index'));
const ClaimsHistoryPage = lazy(() => import('./pages/shared/claims-history/index'));
const BillingHistoryPage = lazy(() => import('./pages/shared/billing-history/index'));

const allRoutesWithLayout: {
   path: string;
   routes: SideBarRouteOption[];
   children: { path: string; element: JSX.Element }[];
}[] = [
   {
      path: '/insurance',
      routes: insuranceRoutes,
      children: [
         {
            path: '',
            element: <InsuranceAnalyticsPage />,
         },
         {
            path: 'history/eligibility-checks',
            element: <EligibilityCheckHistoryPage byInsurance />,
         },
         {
            path: 'history/approvals',
            element: <ApprovalHistoryPage byInsurance />,
         },
         {
            path: 'history/claims',
            element: <ClaimsHistoryPage byInsurance />,
         },
         {
            path: 'history/billing',
            element: <BillingHistoryPage byInsurance />,
         },
         {
            path: 'settings',
            element: <InsuranceSettings />,
         },
         {
            path: 'profile',
            element: <ProfileView />,
         },
         {
            path: 'approval-requests',
            element: <InsuranceServiceRequests />,
         },
         // {
         //    path: 'bills',
         //    element: <BillPage byInsurance />,
         // },
         // {
         //    path: 'bills/per-hospital',
         //    element: <BillPage byInsurance />,
         // },
         // {
         //    path: 'bills/per-hospital/:hospitalSlug',
         //    element: <BillPage byInsurance />,
         // },
         {
            path: 'reports',
            element: <ReportsPage />,
         },
      ],
   },
   {
      path: '/moh-admin',
      routes: mohAdminRoutes,
      children: [
         {
            path: '',
            element: <MOHAdminAnalytics />,
         },
         {
            path: 'profile',
            element: <ProfileView />,
         },
         {
            path: 'insurances/new',
            element: <CreateInsuranceInstitution />,
         },
         {
            path: 'insurances/edit/:id',
            element: <CreateInsuranceInstitution editing />,
         },
         // {
         //    path: 'bills',
         //    element: <BillPage />,
         // },
         {
            path: 'history/eligibility-checks',
            element: <EligibilityCheckHistoryPage />,
         },
         {
            path: 'history/approvals',
            element: <ApprovalHistoryPage />,
         },
         {
            path: 'history/claims',
            element: <ClaimsHistoryPage />,
         },
         {
            path: 'history/billing',
            element: <BillingHistoryPage />,
         },
         // {
         //    path: 'approval-requests',
         //    element: <ApprovalRequests />,
         // },
         // Insurances
         {
            path: 'insurances',
            element: <InsurancesPage />,
         },
         {
            path: 'tokens',
            element: <MOHAdminTokensPage />,
         },
         //medical items
         // {
         //    path: 'medical-items',
         //    element: <MedicalItemsOverview />,
         // },
         // {
         //    path: 'medical-items/medical-acts',
         //    element: <MOHAdminMedicalActs />,
         // },
         // {
         //    path: 'medical-items/consumables',
         //    element: <MOHAdminConsumables />,
         // },
         // {
         //    path: 'medical-items/drugs',
         //    element: <MOHAdminDrugs />,
         // },
         // Tariffs
         // {
         //    path: 'tariffs',
         //    element: <TariffsOverview />,
         // },
         // {
         //    path: 'tariffs/medical-acts',
         //    element: <MOHAdminMedicalActTariffs />,
         // },
         // {
         //    path: 'tariffs/consumables',
         //    element: <MOHAdminConsumableTariffs />,
         // },
         // {
         //    path: 'tariffs/drugs',
         //    element: <MOHAdminDrugTariffs />,
         // },
         {
            path: 'system-settings/users',
            element: <MOHAdminSystemUsers />,
         },
         {
            path: 'system-settings/roles',
            element: <MOHAdminRoles />,
         },
         {
            path: 'rssb-mocks/practitioners',
            element: <RMPractitionerPage />,
         },
         {
            path: 'rssb-mocks/facilities',
            element: <RMFacilityPage />,
         },
         {
            path: 'rssb-mocks/vouchers',
            element: <RMVouchersPage />,
         },
         {
            path: 'rssb-mocks/reports',
            element: <RMReportsPage />,
         },
         {
            path: 'reports',
            element: <ReportsPage />,
         },
         {
            path: 'eligibility-test',
            element: <EligibilityTestPage />,
         },
      ],
   },
   // common routes
   {
      path: '/',
      routes: [],
      children: [
         {
            path: 'bills/viewBill',
            element: <ViewBillPage />,
         },
      ],
   },
];

const AuthRoute = () => {
   const token = Cookies.get('token');
   const location = useLocation();
   const navigate = useNavigate();
   const { pathname } = location;
   const { user } = useAuth();

   useEffect(() => {
      if (!pathname.includes('/auth/') && token) {
         const allowedRoutes = user?.roles
            ?.map((role) => ROLE_TO_ROUTES[role.roleName])
            .flat()
            .concat(ROLE_TO_ROUTES.common);
         if (!allowedRoutes?.some((route) => pathname.startsWith(route))) {
            navigate('/');
            // console.log('not allowed', pathname, user?.roles.map((el) => el.roleName), allowedRoutes);
         }
      }
   }, [navigate, pathname, token, user?.roles]);

   if (token) {
      return <Outlet />;
   }
   return <Navigate to="/auth/login" />;
};

export default function AppRoutes() {
   return (
      <Routes>
         <Route
            path="/"
            element={
               <SuspenseWithLoader>
                  <IndexPage />
               </SuspenseWithLoader>
            }
         />
         <Route
            path="/auth/login"
            element={
               <SuspenseWithLoader>
                  <LoginPage />
               </SuspenseWithLoader>
            }
         />
         <Route
            path="/auth/forgot-password"
            element={
               <SuspenseWithLoader>
                  <ResetPassword />
               </SuspenseWithLoader>
            }
         />
         <Route
            path="/auth/reset-password/:code"
            element={
               <SuspenseWithLoader>
                  <VerifyResetCode />
               </SuspenseWithLoader>
            }
         />
         <Route
            path="/auth/verify-email"
            element={
               <SuspenseWithLoader>
                  <VerifyEmail />
               </SuspenseWithLoader>
            }
         />
         <Route
            path="/auth/accept-invitation"
            element={
               <SuspenseWithLoader>
                  <AcceptInvitation />
               </SuspenseWithLoader>
            }
         />
         {/* Protected Auth Routes */}
         <Route
            element={
               <SuspenseWithLoader>
                  <AuthRoute />
               </SuspenseWithLoader>
            }
         >
            {allRoutesWithLayout.map((route) => (
               <Route
                  key={route.path}
                  path={route.path}
                  element={
                     <SuspenseWithLoader>
                        <MainLayout routes={route.routes} />
                     </SuspenseWithLoader>
                  }
               >
                  {route.children.map((child) => (
                     <Route
                        key={child.path}
                        path={child.path}
                        element={<SuspenseWithLoader>{child.element}</SuspenseWithLoader>}
                     />
                  ))}
               </Route>
            ))}
            <Route
               path="/profile"
               element={
                  <SuspenseWithLoader>
                     <ProfileView />
                  </SuspenseWithLoader>
               }
            />
         </Route>
         <Route
            path="*"
            element={
               <SuspenseWithLoader>
                  <NotFound />
               </SuspenseWithLoader>
            }
         />
      </Routes>
   );
}
